import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ORIGEM_ALUNO, type OrigemAluno } from '@arena/shared/dominio';
import {
  ROTULO_ORIGEM_ALUNO,
  ROTULO_STATUS_ALUNO,
  ehMenorDeIdade,
  zNovoAlunoEm,
  type NovoAluno,
  type StatusInicial,
} from '@arena/shared/schemas';
import { alunosApi } from '../api/alunos.js';
import { ErroDeApi, mensagemDoErro } from '../api/cliente.js';
import { useAtrasado } from '../agenda/useAtrasado.js';
import { Icone } from '../ui/Icone.js';
import { CorpoDeModal, Modal } from '../ui/Modal.jsx';
import { Botao, Campo, Formulario } from '../ui/primitivos.jsx';
import { useToast } from '../ui/Toast.jsx';
import { errosPorCampo, primeiraMensagem } from './formatos.js';

/**
 * `#m-novo-aluno`.
 *
 * O protótipo também oferece Plano e Turma aqui — os dois chegam com os
 * documentos 12 e 10, fora do escopo desta entrega (08-alunos.md,
 * "Fora"), e por isso não aparecem no formulário: um campo que não grava
 * nada é pior do que a ausência dele.
 *
 * A validação é a MESMA função do servidor, `zNovoAlunoEm(hoje)` de
 * `@arena/shared/schemas` — "hoje" vem de `GET /alunos/indicadores`, no
 * fuso da arena, porque é dele que depende a regra do menor de idade.
 */
export function ModalDeNovoAluno({
  onFechar,
  onCriado,
}: {
  onFechar: () => void;
  onCriado: (id: string) => void;
}) {
  const avisar = useToast();
  const cliente = useQueryClient();
  const indicadores = useQuery({
    queryKey: ['alunos', 'indicadores'],
    queryFn: () => alunosApi.indicadores(),
  });

  const [nome, setNome] = useState('');
  const [telefone, setTelefone] = useState('');
  const [nascimento, setNascimento] = useState('');
  const [status, setStatus] = useState<StatusInicial>('Experimental');
  const [cpf, setCpf] = useState('');
  const [email, setEmail] = useState('');
  const [origem, setOrigem] = useState<OrigemAluno | ''>('');
  const [contatoEmergenciaNome, setContatoEmergenciaNome] = useState('');
  const [contatoEmergenciaTelefone, setContatoEmergenciaTelefone] = useState('');
  const [responsavelFinanceiro, setResponsavelFinanceiro] = useState('');
  const [observacoesMedicas, setObservacoesMedicas] = useState('');
  const [aceiteTermo, setAceiteTermo] = useState(false);
  const [consentimentoImagem, setConsentimentoImagem] = useState(false);
  const [erros, setErros] = useState<Record<string, string>>({});
  const [erroGeral, setErroGeral] = useState<string | null>(null);

  const telefoneAtrasado = useAtrasado(telefone.replace(/\D/g, ''), 400);
  const duplicados = useQuery({
    queryKey: ['alunos', 'duplicados', telefoneAtrasado],
    queryFn: () => alunosApi.duplicados(telefoneAtrasado),
    enabled: telefoneAtrasado.length === 10 || telefoneAtrasado.length === 11,
    staleTime: 10_000,
  });

  const criar = useMutation({
    mutationFn: (dados: NovoAluno) => alunosApi.criar(dados),
    onSuccess: () => cliente.invalidateQueries({ queryKey: ['alunos'] }),
  });

  const hoje = indicadores.data?.hoje;
  const menor = nascimento && hoje ? ehMenorDeIdade(nascimento, hoje) : false;

  async function salvar() {
    setErros({});
    setErroGeral(null);
    if (!hoje) return;

    const dados = {
      nome,
      telefone,
      nascimento,
      status,
      cpf: cpf.trim() || undefined,
      email: email.trim() || undefined,
      origem: origem || undefined,
      contatoEmergenciaNome: contatoEmergenciaNome.trim() || undefined,
      contatoEmergenciaTelefone: contatoEmergenciaTelefone.trim() || undefined,
      responsavelFinanceiro: responsavelFinanceiro.trim() || undefined,
      observacoesMedicas: observacoesMedicas.trim() || undefined,
      aceiteTermo,
      consentimentoImagem,
    };

    const pedido = zNovoAlunoEm(hoje).safeParse(dados);
    if (!pedido.success) {
      setErros(errosPorCampo(pedido.error.issues));
      return;
    }

    try {
      const ficha = await criar.mutateAsync(pedido.data);
      avisar(`Aluno cadastrado com status ${ficha.status}.`);
      onCriado(ficha.id);
    } catch (erro) {
      if (erro instanceof ErroDeApi && erro.campos) setErros(primeiraMensagem(erro.campos));
      setErroGeral(mensagemDoErro(erro));
    }
  }

  return (
    <Modal
      aberto
      onFechar={onFechar}
      titulo="Novo aluno"
      descricao="Campos obrigatórios: nome, telefone, nascimento e status."
      rodape={
        <>
          <Botao variante="fantasma" onClick={onFechar}>
            Cancelar
          </Botao>
          <Botao onClick={() => void salvar()} disabled={criar.isPending || !hoje}>
            {criar.isPending ? 'Cadastrando…' : 'Cadastrar aluno'}
          </Botao>
        </>
      }
    >
      <CorpoDeModal>
        <Formulario colunas={2} onSubmit={(e) => e.preventDefault()}>
          <Campo rotulo="Nome completo *" erro={erros['nome']}>
            <input
              placeholder="Nome e sobrenome"
              value={nome}
              onChange={(e) => setNome(e.target.value)}
              autoFocus
            />
          </Campo>
          <Campo
            rotulo="Telefone *"
            erro={erros['telefone']}
            ajuda={
              duplicados.data && duplicados.data.alunos.length > 0
                ? `Já cadastrado: ${duplicados.data.alunos
                    .map((a) => `${a.nome} (${ROTULO_STATUS_ALUNO[a.status]})`)
                    .join(', ')}.`
                : undefined
            }
          >
            <input
              placeholder="(62) 9 0000-0000"
              value={telefone}
              onChange={(e) => setTelefone(e.target.value)}
            />
          </Campo>
          <Campo
            rotulo="Data de nascimento *"
            erro={erros['nascimento']}
            ajuda={
              menor ? 'Aluno menor de idade: o responsável financeiro é obrigatório.' : undefined
            }
          >
            <input type="date" value={nascimento} onChange={(e) => setNascimento(e.target.value)} />
          </Campo>
          <Campo rotulo="Status *" erro={erros['status']}>
            <select value={status} onChange={(e) => setStatus(e.target.value as StatusInicial)}>
              <option value="Experimental">Experimental</option>
              <option value="Ativo">Ativo</option>
            </select>
          </Campo>
          <Campo rotulo="CPF" erro={erros['cpf']}>
            <input placeholder="opcional" value={cpf} onChange={(e) => setCpf(e.target.value)} />
          </Campo>
          <Campo rotulo="E-mail" erro={erros['email']}>
            <input
              placeholder="opcional"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </Campo>
          <Campo rotulo="Como conheceu a arena" erro={erros['origem']}>
            <select value={origem} onChange={(e) => setOrigem(e.target.value as OrigemAluno | '')}>
              <option value="">Não informado</option>
              {ORIGEM_ALUNO.filter((o) => o !== 'Importacao').map((o) => (
                <option key={o} value={o}>
                  {ROTULO_ORIGEM_ALUNO[o]}
                </option>
              ))}
            </select>
          </Campo>
          <Campo rotulo="Contato de emergência" erro={erros['contatoEmergenciaNome']}>
            <input
              placeholder="Nome"
              value={contatoEmergenciaNome}
              onChange={(e) => setContatoEmergenciaNome(e.target.value)}
            />
          </Campo>
          <Campo rotulo="Telefone do contato" erro={erros['contatoEmergenciaTelefone']}>
            <input
              placeholder="opcional"
              value={contatoEmergenciaTelefone}
              onChange={(e) => setContatoEmergenciaTelefone(e.target.value)}
            />
          </Campo>
        </Formulario>

        <Campo
          rotulo="Responsável financeiro"
          erro={erros['responsavelFinanceiro']}
          ajuda="Preencher quando o aluno for menor de idade"
        >
          <input
            placeholder="Nome do responsável"
            value={responsavelFinanceiro}
            onChange={(e) => setResponsavelFinanceiro(e.target.value)}
          />
        </Campo>

        {indicadores.data?.veSaude && (
          <Campo rotulo="Observações médicas e restrições" erro={erros['observacoesMedicas']}>
            <textarea
              rows={2}
              placeholder="Lesões, restrições, alergias…"
              value={observacoesMedicas}
              onChange={(e) => setObservacoesMedicas(e.target.value)}
            />
          </Campo>
        )}

        <label className="consent">
          <input
            className="ck"
            type="checkbox"
            checked={aceiteTermo}
            onChange={(e) => setAceiteTermo(e.target.checked)}
          />
          <span>
            Aceite do termo de uso e da política de privacidade *
            <small>
              Registra data, hora, IP e a versão {indicadores.data?.versaoTermoVigente ?? ''} do
              termo aceito
            </small>
          </span>
        </label>
        {erros['aceiteTermo'] && (
          <div className="help" style={{ color: 'var(--red-lt)' }} role="alert">
            {erros['aceiteTermo']}
          </div>
        )}
        <label className="consent">
          <input
            className="ck"
            type="checkbox"
            checked={consentimentoImagem}
            onChange={(e) => setConsentimentoImagem(e.target.checked)}
          />
          <span>
            Consentimento de uso de imagem
            <small>Separado do termo principal e revogável a qualquer momento</small>
          </span>
        </label>

        {erroGeral && (
          <div className="aviso conflito" role="alert">
            <Icone nome="info" />
            <div>{erroGeral}</div>
          </div>
        )}
      </CorpoDeModal>
    </Modal>
  );
}
